import LegacyRouteView from "@wkt/components/shell/LegacyRouteView.vue";
import { login as startKeycloakLogin } from "@wkt/lib/keycloak";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { useShellStore } from "@wkt/stores/shell";
import {
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw,
} from "vue-router";
import {
  getShellModeForRouteName,
  getVueModuleForRouteName,
  moduleRouteDefinitions,
} from "./moduleRegistry";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "bridge-root",
    component: LegacyRouteView,
    meta: {
      shellMode: "none",
      requiresSession: false,
    },
  },
  ...moduleRouteDefinitions.map((definition) => ({
    path: definition.path,
    name: definition.name,
    component: LegacyRouteView,
    meta: {
      vueModule: definition.vueModule,
      shellMode: definition.shellMode,
      requiresSession: definition.requiresSession,
      requiresModule: definition.requiresModule,
      requiresLicense: definition.requiresLicense,
    },
  })),
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

function isModuleEnabled(company: any, moduleKey: unknown): boolean {
  if (!company || typeof moduleKey !== "string") {
    return true;
  }

  const readBooleanFlag = (keys: string[], fallback: boolean): boolean => {
    for (const key of keys) {
      const value = company[key];
      if (typeof value === "boolean") {
        return value;
      }
    }

    return fallback;
  };

  switch (moduleKey) {
    case "checkin":
      return readBooleanFlag(["moduleCheckinEnabled"], true);
    case "documents":
      return readBooleanFlag(
        ["moduleDocumentsEnabled", "isModuleDocumentsEnabled"],
        true,
      );
    case "procedures":
      return readBooleanFlag(
        ["moduleProceduresEnabled", "isModuleProceduresEnabled"],
        true,
      );
    case "expenses":
      return readBooleanFlag(
        ["moduleExpensesEnabled", "isModuleExpensesEnabled"],
        false,
      );
    case "training":
      return readBooleanFlag(["trainingModuleEnabled"], false);
    default:
      return true;
  }
}

function hasLicense(_company: any, _requiredLicense: unknown): boolean {
  return true;
}

const KIOSK_SAFE_ROUTES = new Set([
  "home",
  "checkin",
  "procedures",
  "documents",
  "docbycategory",
  "form",
  "profile",
  "hcalendar",
  "expensesheets",
  "expensesheetdetail",
  "training",
  "vacations",
  "vacationsdetail",
  "employeecalendar",
  "checkinhistory",
  "checkinhistorydetail",
  "activitydetail",
  "targethours",
  "projects",
  "employee",
  "survey",
  "review",
  "kiosk",
]);

function isKioskSafeRoute(routeName: unknown): boolean {
  return typeof routeName === "string" && KIOSK_SAFE_ROUTES.has(routeName);
}

function isColdHashBootstrap(to: { name?: unknown }): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hash = window.location.hash || "";
  if (!hash || hash === "#" || hash === "#/") {
    return false;
  }

  return (
    to.name !== "bridge-root" && to.name !== "register" && to.name !== "kiosk"
  );
}

function hasLoginRequiredErrorFragment(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return /(?:^|&)error=login_required(?:&|$)/i.test(
    (window.location.hash || "").substring(1),
  );
}

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const sessionStore = useSessionCatalogStore();
  const shellStore = useShellStore();
  shellStore.syncKioskFromStorage();

  const targetVueModule =
    typeof to.meta.vueModule === "string" ? to.meta.vueModule : null;
  const isBridgeInitiatedReveal =
    targetVueModule !== null &&
    shellStore.pendingRevealModule === targetVueModule;

  if (to.meta.requiresSession === true && !sessionStore.isReady) {
    if (isBridgeInitiatedReveal) {
      return true;
    }

    const result = await sessionStore.bootstrapSession();
    switch (result.outcome) {
      case "ready":
        break;
      case "kiosk":
        return to.name === "kiosk" ? true : { name: "kiosk" };
      case "register":
        return to.name === "register" ? true : { name: "register" };
      case "login-required":
        if (!hasLoginRequiredErrorFragment()) {
          void startKeycloakLogin();
        }
        return false;
      case "error":
        if (isColdHashBootstrap(to)) {
          return true;
        }

        return false;
    }
  }

  if (shellStore.isKioskSession && !isKioskSafeRoute(to.name)) {
    return { name: "home" };
  }

  if (
    typeof to.meta.requiresModule === "string" &&
    !isModuleEnabled(sessionStore.company, to.meta.requiresModule)
  ) {
    return { name: "home" };
  }

  if (
    typeof to.meta.requiresLicense === "string" &&
    !hasLicense(sessionStore.company, to.meta.requiresLicense)
  ) {
    return { name: "home" };
  }

  return true;
});

router.afterEach((to) => {
  const shellStore = useShellStore();
  const activeRouteModule = getVueModuleForRouteName(to.name);

  if (
    shellStore.pendingRevealModule &&
    shellStore.pendingRevealModule !== activeRouteModule
  ) {
    shellStore.clearPendingReveal();
  }

  shellStore.setActiveRouteName(typeof to.name === "string" ? to.name : null);
  shellStore.setActiveShellMode(getShellModeForRouteName(to.name));
  shellStore.setCurrentBridgeModule(activeRouteModule);
  shellStore.syncKioskFromStorage();
});
